// RechargeFlow — 3-step wizard for the manual recharge flow.
//
// Step 1 (amount):  pick ₹ via input + quick chips (50/100/200/500/1000/2000)
// Step 2 (pay):     show PaymentReceiverCard with the receiver's UPI/QR.
//                   The player goes to their UPI app, pays, copies the txn id.
// Step 3 (confirm): paste txn id + optional note, submit.
// Step 4 (success): "Pending review — request #N" with what-happens-next
//                   guidance and a Close button.
//
// Failure modes:
//   - duplicate txn id → 409 → friendly "already submitted" message
//   - no payment_config → step 2 shows the fallback contact card
//   - validation errors surface inline
//
// The whole modal is a portal-style fixed overlay; backdrop click +
// Esc + the X button all close. Scroll-locks the body when open.

import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useToast } from '../lib/toast';
import Icon from './Icon';
import PaymentReceiverCard, { type PaymentConfig } from './PaymentReceiverCard';
import ProofImagePicker from './ProofImagePicker';
// Cascade Recharge Lock (Phase R7 follow-up, 2026-05-14). When the
// server responds with parent_locked=true the wizard swaps the QR
// surface for a calm "temporarily unavailable" panel and prevents
// the user from advancing to Step 3. The submit endpoint is
// intentionally NOT gated server-side (see depositGuards header),
// so the only thing keeping the user on Step 2 is this UI gate —
// good enough because any user who genuinely already paid will be
// surfacing a stale Step 2 from before suspension and will have
// progressed past it before the lock landed.
import RechargeLockedPanel from './RechargeLockedPanel';

type Step = 1 | 2 | 3 | 'success';

// Quick-pick chips for the amount step. Min recharge is ₹200, then
// double-up steps to ₹10,000. Manual entry below 200 is blocked
// from advancing past Step 1 (canSubmit / Next button checks).
const QUICK_AMOUNTS = [200, 500, 1000, 2000, 5000, 10000];
const MIN_AMOUNT = 200;
const MAX_AMOUNT = 100000;

export default function RechargeFlow({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void; // called after successful submit (so Wallet can refresh)
}) {
  const t = useT();
  const { push } = useToast();

  const [step, setStep] = useState<Step>(1);
  const [amount, setAmount] = useState<number>(500);
  const [txnId, setTxnId] = useState('');
  const [note, setNote] = useState('');
  const [cfg, setCfg] = useState<PaymentConfig | null>(null);
  const [cfgErr, setCfgErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  // Phase 18 — optional payment screenshot. Browser-side canvas
  // re-encode happens inside ProofImagePicker; we receive a clean
  // JPEG Blob ready to ship.
  const [proofBlob, setProofBlob] = useState<Blob | null>(null);
  // 2026-05-21 — Step 2 confirm gate. The player must actually open
  // a UPI app before the "I have paid" button reveals itself. This
  // pattern is the cheapest defense against the "I'll just click
  // Next without paying" mis-step we kept seeing in field testing.
  //
  // initiatedAt — millis timestamp set when PaymentReceiverCard
  //   fires onPaymentInitiated (= a UPI app deep link was tapped).
  // canConfirmPayment — true once EITHER (a) ~1.6 s have elapsed
  //   after initiatedAt OR (b) the page was hidden then visible
  //   again (= player came back from the UPI app). Whichever fires
  //   first wins.
  const [paymentInitiatedAt, setPaymentInitiatedAt] = useState<number | null>(null);
  const [canConfirmPayment, setCanConfirmPayment] = useState(false);

  // On open: load the payment config + reset wizard state.
  useEffect(() => {
    if (!open) return;
    setStep(1); setTxnId(''); setNote('');
    setSubmitErr(null); setSubmittedId(null);
    setCfg(null); setCfgErr(null);
    setPaymentInitiatedAt(null); setCanConfirmPayment(false);
    setProofBlob(null);
    api.get<PaymentConfig>('/me/payment-config')
      .then(setCfg)
      .catch((e: ApiError) => setCfgErr(e.message));
    // body scroll lock
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ─── "I have paid" reveal logic ─────────────────────────────────
  // Two paths flip canConfirmPayment from false to true:
  //   (1) A short 1.6-second timer after the player tapped a UPI
  //       app deep link. In-app browsers (FB / Insta webview, MIUI
  //       browser) often suppress the visibilitychange event so we
  //       can't rely on it alone — the timer is the universal
  //       fallback.
  //   (2) visibilitychange firing from hidden → visible. On modern
  //       Chrome/Safari this beats the timer to the punch when the
  //       payment app was actually launched, so the button is ready
  //       the instant the player swipes back to us.
  // Either path is sufficient; we don't reset on a re-open of the
  // wizard because the close-then-reopen flow already wipes
  // paymentInitiatedAt back to null above.
  useEffect(() => {
    if (!open) return;
    if (paymentInitiatedAt == null) return;
    if (canConfirmPayment) return;
    const timer = window.setTimeout(() => setCanConfirmPayment(true), 1600);
    const onVis = () => {
      if (document.visibilityState === 'visible') setCanConfirmPayment(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [open, paymentInitiatedAt, canConfirmPayment]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function submit() {
    setBusy(true); setSubmitErr(null);
    try {
      // 2026-05-21 — Either-or submit body. If the player provided a
      // transaction ID we send it as before; if they only attached a
      // screenshot we send transaction_id=null and flag the upcoming
      // proof so the backend Zod .refine() accepts the body. The
      // screenshot itself is POSTed in the follow-up call below.
      const trimmed = txnId.trim();
      const hasTxn  = trimmed.length >= 8;
      const r = await api.post<{ request: { id: number } }>(
        '/me/recharge',
        {
          amount,
          transaction_id: hasTxn ? trimmed : null,
          has_proof_pending: !hasTxn && !!proofBlob,
          user_note: note.trim() || null,
        }
      );
      setSubmittedId(r.request.id);
      // Phase 18 — if the player picked a screenshot, post it to
      // /me/recharge/:id/proof. Failure here is NON-FATAL: the
      // request itself was created successfully, the player just
      // has up to 24h to upload from their Wallet history.
      if (proofBlob) {
        try {
          const fd = new FormData();
          fd.append('proof', proofBlob, 'screenshot.jpg');
          await api.postForm(`/me/recharge/${r.request.id}/proof`, fd);
        } catch (uploadErr) {
          push('error',
            'Request created, but screenshot upload failed. ' +
            'Open Wallet → history to retry within 24 hours.');
        }
      }
      setStep('success');
      push('success', t('recharge.toast.submitted'));
      onCompleted?.();
    } catch (e) {
      // Surface zod validation issues in the UI instead of the generic
      // "Validation error" message. The backend returns
      // { code: 'bad_request', message: 'Validation error', details: { issues: [...] } }
      // — the issue's own .message is far more actionable for the player
      // ("Provide either a transaction ID or a payment screenshot.").
      const err = e as ApiError;
      let msg = err.message || 'Submit failed';
      const issues = (err.details as { issues?: Array<{ message?: string; path?: (string|number)[] }> } | undefined)?.issues;
      if (issues && issues.length) {
        const first = issues[0];
        if (first?.message) msg = first.message;
      }
      setSubmitErr(msg);
    } finally { setBusy(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm animate-slide-down"
      onClick={onClose}
      role="dialog" aria-modal="true" aria-label={t('recharge.title')}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[92dvh] flex flex-col
                   rounded-3xl overflow-hidden
                   bg-white dark:bg-arena-800
                   border border-arena-200 dark:border-neon-400/25
                   shadow-2xl"
      >
        {/* Header */}
        <Header step={step} onClose={onClose} t={t} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4">
          {step === 1 && (
            <Step1Amount
              amount={amount} setAmount={setAmount} t={t}
              remainingCapacity={cfg?.remaining_capacity ?? null}
              /* Tapping a quick chip picks the amount AND jumps to the
                 payment step — one tap instead of chip-then-Next
                 (2026-08-06 UX sweep). */
              onQuickPick={(a) => { setAmount(a); setStep(2); }}
            />
          )}

          {step === 2 && (
            <Step2Pay
              cfg={cfg} cfgErr={cfgErr} amount={amount} t={t} onClose={onClose}
              paymentInitiated={paymentInitiatedAt != null}
              canConfirmPayment={canConfirmPayment}
              onPaymentInitiated={() => setPaymentInitiatedAt(Date.now())}
            />
          )}

          {step === 3 && (
            <Step3Confirm
              cfg={cfg} amount={amount}
              txnId={txnId} setTxnId={setTxnId}
              note={note} setNote={setNote}
              proofBlob={proofBlob} setProofBlob={setProofBlob}
              setSubmitErr={setSubmitErr}
              error={submitErr}
              t={t}
            />
          )}

          {step === 'success' && (
            <StepSuccess requestId={submittedId} amount={amount} t={t} />
          )}
        </div>

        {/* Footer */}
        {/* Cascade Recharge Lock — when the server has flagged the
            parent as suspended, the entire footer is suppressed on
            Step 2 because the user must NOT progress to Step 3.
            RechargeLockedPanel renders its own Close CTA, so the
            modal is still dismissable. */}
        <Footer
          step={step} busy={busy}
          amount={amount}
          canPayHere={!!(cfg && cfg.configured)}
          // 2026-05-21 — Either-or submit gate. The player can now
          // submit with EITHER a transaction ID OR a screenshot
          // attached. The UI on Step 3 emphasises screenshot first,
          // but the backend / admin queue accept either form of
          // evidence. canSubmit gates the Submit-for-review button.
          canSubmit={(!!proofBlob || txnId.trim().length >= 8)
                     && amount >= MIN_AMOUNT && amount <= MAX_AMOUNT}
          parentLocked={!!cfg?.parent_locked}
          // 2026-05-21 — Step 2's confirm button only renders once the
          // player has actually tapped a UPI app. See the gate logic
          // earlier in this component for the two reveal paths.
          step2CanConfirm={canConfirmPayment}
          onBack={() => setStep((s) => (s === 3 ? 2 : s === 2 ? 1 : s))}
          onNext={() => setStep((s) => (s === 1 ? 2 : s === 2 ? 3 : s))}
          onSubmit={submit}
          onClose={onClose}
          t={t}
        />
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function Header({ step, onClose, t }: { step: Step; onClose: () => void; t: ReturnType<typeof useT> }) {
  // Stepper at the top, except on success where we hide it.
  const isSuccess = step === 'success';
  return (
    <div className="px-4 pt-4 pb-3 border-b border-arena-200 dark:border-arena-600/40 shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center
                        bg-gradient-to-br from-brand-300/40 to-brand-500/30
                        text-brand-700 dark:text-brand-300">
          <Icon name="add_circle" size={22} filled />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-extrabold text-base text-arena-800 dark:text-neon-100 leading-tight">
            {t('recharge.title')}
          </h2>
          {!isSuccess && (
            <p className="text-[11px] text-arena-500 dark:text-neon-200/60 leading-tight">
              {t(`recharge.step${step}.subtitle`)}
            </p>
          )}
        </div>
        <button onClick={onClose} aria-label={t('common.close')}
          className="shrink-0 w-9 h-9 rounded-full grid place-items-center
                     text-arena-500 dark:text-neon-200/70
                     hover:bg-arena-100 dark:hover:bg-arena-700/60">
          <Icon name="close" size={20} />
        </button>
      </div>

      {/* Stepper dots */}
      {!isSuccess && (
        <div className="mt-3 flex items-center gap-1">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex-1 flex items-center gap-1">
              <div className={`flex-1 h-1.5 rounded-full ${
                (step as number) >= n ? 'bg-brand-500' : 'bg-arena-200 dark:bg-arena-700/60'
              }`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Step1Amount({ amount, setAmount, t, remainingCapacity, onQuickPick }: {
  amount: number; setAmount: (n: number) => void;
  onQuickPick: (n: number) => void;
  t: ReturnType<typeof useT>;
  // Cascade Recharge Lock — Dynamic Capacity. When the parent has a
  // negative wallet but enough cushion left to support a recharge
  // above MIN_AMOUNT, the server pushes the remaining capacity here.
  //   null  → no cap (parent healthy); use MAX_AMOUNT as today.
  //   N>0   → cap chips above N + clamp the input + show "Max
  //           available" banner so the player understands why they
  //           can't go higher.
  // The server ALSO gates POST /me/recharge on this number — the UI
  // cap is for friendly UX; the server is the source of truth.
  remainingCapacity: number | null;
}) {
  // effectiveMax respects whichever is smaller: the platform-wide
  // hard cap or the parent's live capacity. When capacity is null we
  // simply use MAX_AMOUNT — happy-path behavior unchanged.
  const effectiveMax = remainingCapacity != null
    ? Math.min(MAX_AMOUNT, remainingCapacity)
    : MAX_AMOUNT;
  // Whether to show the "Max available" banner. We don't show it for
  // the happy path because clutter > usefulness when no cap exists.
  const showCapBanner = remainingCapacity != null;

  return (
    <div className="space-y-3">
      {showCapBanner && (
        // Soft amber notice — calmer than rose, more attention than
        // brand. The user is NOT in trouble themselves; they just
        // have a temporary upper bound on the recharge.
        <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10
                        border border-amber-300 dark:border-amber-400/30
                        px-3 py-2 flex items-start gap-2">
          <Icon name="info" size={16} className="text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" filled />
          <div className="text-[12.5px] leading-snug text-amber-900 dark:text-amber-100">
            {t('recharge.cap.banner', {
              max: '₹' + (remainingCapacity as number).toLocaleString('en-IN'),
            })}
          </div>
        </div>
      )}

      <label className="block">
        <span className="block text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1.5">
          {t('recharge.step1.amount')}
        </span>
        <div className="flex items-center gap-2 rounded-2xl border border-arena-200 dark:border-arena-600/40
                        bg-arena-50/60 dark:bg-arena-900/40 px-4 py-3">
          <span className="text-2xl font-display font-black text-arena-800 dark:text-neon-100">₹</span>
          <input
            type="number" inputMode="numeric"
            max={effectiveMax}
            value={amount || ''}
            // Clamp on every change so the field can never hold a
            // value above the cap. The server WILL reject anyway
            // (defense in depth), but clamping client-side avoids
            // the "I typed 5000 and submit failed" surprise.
            onChange={(e) => setAmount(Math.max(0, Math.min(effectiveMax, Number(e.target.value) || 0)))}
            placeholder="0"
            className="flex-1 bg-transparent outline-none text-3xl font-display font-black tabular-nums text-arena-800 dark:text-neon-100"
          />
        </div>
        <p className="text-[11px] mt-1">
          {/* Tiny orange highlight so the player knows the floor at
              a glance — visible but unobtrusive. */}
          <span className="font-bold text-brand-600 dark:text-brand-400">
            {t('recharge.step1.minHint', { min: '₹' + MIN_AMOUNT })}
          </span>
          <span className="text-arena-500 dark:text-neon-200/60">
            {' · '}
            {t('recharge.step1.maxHint', { max: '₹' + effectiveMax.toLocaleString('en-IN') })}
          </span>
        </p>
      </label>

      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1.5">
          {t('recharge.step1.quickPick')}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {/* Filter out chips that exceed the live cap. With a 1000
              cap, only ₹200 / ₹500 / ₹1000 render. If the cap is
              between MIN and the smallest chip (e.g. 350) NONE will
              show — that's fine, the user can type the cap value
              manually into the input above. */}
          {QUICK_AMOUNTS.filter((a) => a <= effectiveMax).map((a) => (
            <button
              key={a} type="button"
              onClick={() => onQuickPick(a)}
              className={`py-2.5 rounded-xl font-bold tabular-nums text-sm border transition active:scale-[.98] ${
                amount === a
                  ? 'bg-brand-500 border-brand-600 text-white shadow'
                  : 'bg-white dark:bg-arena-900/40 border-arena-200 dark:border-arena-600/40 text-arena-800 dark:text-neon-100 hover:border-brand-400/50'
              }`}
            >
              ₹{a.toLocaleString('en-IN')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step2Pay({
  cfg, cfgErr, amount, t, onClose,
  paymentInitiated, canConfirmPayment, onPaymentInitiated,
}: {
  cfg: PaymentConfig | null; cfgErr: string | null; amount: number;
  t: ReturnType<typeof useT>;
  // onClose threaded through so RechargeLockedPanel can offer a
  // single explicit "Close" CTA — the wizard footer hides Next/Back
  // when the lock fires (see Footer below).
  onClose: () => void;
  // 2026-05-21 — Two pieces of state from the parent flow:
  //   paymentInitiated: true after the player taps a UPI app (the
  //     deep link fires, the OS hands off, we mark the moment).
  //   canConfirmPayment: true after the wait/visibility gate has
  //     cleared, which is when the footer "I have paid" button
  //     should become available.
  // The Step 2 body uses paymentInitiated to swap the "open a UPI
  // app to continue" nudge for a calmer "did you finish paying?"
  // hint; the footer (separate component) uses canConfirmPayment
  // to render its big confirm button.
  paymentInitiated: boolean;
  canConfirmPayment: boolean;
  onPaymentInitiated: () => void;
}) {
  if (cfgErr) {
    return (
      <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30
                      text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">{cfgErr}</div>
    );
  }
  if (!cfg) {
    return <div className="text-center py-8 text-arena-500 dark:text-neon-200/60">{t('common.loading')}</div>;
  }
  // Cascade Recharge Lock OR not-configured — both swap the QR
  // surface entirely. The amount pill above the QR also disappears
  // (showing it next to an "outage" or "setup pending" message would
  // imply the user should still try to send that amount). Variant
  // is driven by lock_reason so the calm-panel copy matches the
  // actual cause: 'not_configured' uses the gentler "recharge will
  // be available soon" wording; everything else uses the existing
  // cascade-lock copy.
  if (cfg.parent_locked) {
    return (
      <RechargeLockedPanel
        onClose={onClose}
        variant={cfg.lock_reason === 'not_configured' ? 'not_configured' : 'cascade_lock'}
      />
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-brand-100/60 dark:bg-brand-500/10 border border-brand-300 dark:border-brand-400/30
                      px-4 py-2.5 text-center">
        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-700 dark:text-brand-300">
          {t('recharge.step2.payAmount')}
        </div>
        <div className="font-display font-black text-3xl text-brand-700 dark:text-brand-300 tabular-nums">
          ₹{amount.toLocaleString('en-IN')}
        </div>
      </div>

      {/* Pre-payment nudge: until the player has tapped a UPI app,
          this amber hint sits above the QR / app picker so the next
          action is unambiguous even for non-readers — the camera +
          arrow + "Pay Now" line below points at the receiver card's
          "Open UPI app" button. */}
      {!paymentInitiated && (
        <div className="rounded-xl border border-amber-300 dark:border-amber-400/40
                        bg-amber-50/80 dark:bg-amber-500/10 px-3 py-2.5
                        flex items-start gap-2">
          <Icon name="touch_app" size={20} filled className="text-amber-700 dark:text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[12px] leading-snug text-amber-900 dark:text-amber-100">
            <div className="font-bold">{t('recharge.step2.payFirst.title')}</div>
            <div className="opacity-90">{t('recharge.step2.payFirst.body')}</div>
          </div>
        </div>
      )}

      <PaymentReceiverCard cfg={cfg} amount={amount} onPaymentInitiated={onPaymentInitiated} />

      {/* Post-payment nudge: shown after the deep link fires. While
          the reveal timer ticks (1.6s) we show a spinner; once
          canConfirmPayment flips, the message swaps to "tap I have
          paid below". This shows the player something IS happening
          while they wait. */}
      {paymentInitiated && (
        <div className={`rounded-xl px-3 py-2.5 flex items-start gap-2 border
                         ${canConfirmPayment
                           ? 'bg-emerald-50/80 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-400/40'
                           : 'bg-cyan-50/80 dark:bg-cyan-500/10 border-cyan-300 dark:border-cyan-400/40'}`}>
          {canConfirmPayment ? (
            <>
              <Icon name="check_circle" size={20} filled
                    className="text-emerald-700 dark:text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-[12px] leading-snug text-emerald-900 dark:text-emerald-100">
                <div className="font-bold">{t('recharge.step2.didYouPay.title')}</div>
                <div className="opacity-90">{t('recharge.step2.didYouPay.body')}</div>
              </div>
            </>
          ) : (
            <>
              <Icon name="progress_activity" size={20}
                    className="text-cyan-700 dark:text-cyan-300 shrink-0 mt-0.5 animate-spin" />
              <div className="text-[12px] leading-snug text-cyan-900 dark:text-cyan-100">
                <div className="font-bold">{t('recharge.step2.waiting.title')}</div>
                <div className="opacity-90">{t('recharge.step2.waiting.body')}</div>
              </div>
            </>
          )}
        </div>
      )}

      <ol className="text-[12px] text-arena-700 dark:text-neon-200/80 list-decimal pl-5 space-y-1
                     bg-arena-50 dark:bg-arena-900/40 rounded-lg px-4 py-3 border border-arena-200 dark:border-arena-600/40">
        <li>{t('recharge.step2.howto.1')}</li>
        <li>{t('recharge.step2.howto.2')}</li>
        <li>{t('recharge.step2.howto.3')}</li>
      </ol>
    </div>
  );
}

function Step3Confirm({
  cfg, amount, txnId, setTxnId, note, setNote,
  proofBlob, setProofBlob, setSubmitErr, error, t,
}: {
  cfg: PaymentConfig | null; amount: number;
  txnId: string; setTxnId: (s: string) => void;
  note: string; setNote: (s: string) => void;
  // Screenshot upload state lifted into the step so the visually
  // prominent "Step A" card can react to selection (border flips
  // from dashed/inviting to solid/done) and the icon flow underneath
  // collapses once a file is chosen.
  proofBlob: Blob | null;
  setProofBlob: (b: Blob | null) => void;
  setSubmitErr: (s: string | null) => void;
  error: string | null;
  t: ReturnType<typeof useT>;
}) {
  const hasProof = !!proofBlob;
  return (
    <div className="space-y-4">
      {/* Amount-paid header — unchanged anchor at the top so the user
          can confirm they're submitting against the right number. */}
      <div className="rounded-xl bg-brand-100/60 dark:bg-brand-500/10 border border-brand-300 dark:border-brand-400/30
                      px-4 py-2.5 text-center">
        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-700 dark:text-brand-300">
          {t('recharge.step3.amountPaid')}
        </div>
        <div className="font-display font-black text-3xl text-brand-700 dark:text-brand-300 tabular-nums">
          ₹{amount.toLocaleString('en-IN')}
        </div>
      </div>

      {/* ─── STEP A — SCREENSHOT (visually loudest) ───────────────
          Sits directly under the amount card because it's the action
          we want the player to take FIRST. The card is bordered with
          a 2-px emerald ring + subtle glow so the eye lands here
          before anything else on the screen. When `hasProof` flips
          true, the badge swaps from "★ DO THIS FIRST" to a green
          "✓ DONE" pill and the bottom flow guide collapses. */}
      <div className={`relative rounded-2xl overflow-hidden p-4 transition
                       ${hasProof
                         ? 'border-2 border-emerald-500 dark:border-emerald-400/70 bg-emerald-50 dark:bg-emerald-500/15 shadow-[0_4px_18px_-6px_rgba(16,185,129,.45)]'
                         : 'border-2 border-emerald-400 dark:border-emerald-400/60 bg-gradient-to-br from-emerald-50 via-emerald-50/70 to-cyan-50 dark:from-emerald-500/15 dark:via-emerald-500/10 dark:to-cyan-500/10 ring-2 ring-emerald-200/70 dark:ring-emerald-400/20 shadow-[0_6px_24px_-8px_rgba(16,185,129,.5)]'}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-12 h-12 rounded-2xl grid place-items-center
                          bg-emerald-500 text-white shadow-lg shadow-emerald-500/40">
            <Icon name="photo_camera" size={28} filled />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider
                                ${hasProof
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-amber-400 text-amber-950'}`}>
                {hasProof ? <><Icon name="check_circle" size={11} filled /> {t('recharge.step3.proof.done')}</>
                          : <><Icon name="priority_high" size={11} filled /> {t('recharge.step3.proof.firstStep')}</>}
              </span>
            </div>
            <div className="font-display font-extrabold text-base text-emerald-900 dark:text-emerald-100 mt-1 leading-tight">
              {t('recharge.step3.proof.title')}
            </div>
            <div className="text-[11.5px] text-emerald-800/80 dark:text-emerald-200/70 leading-snug mt-0.5">
              {t('recharge.step3.proof.subtitle')}
            </div>
          </div>
        </div>

        <ProofImagePicker
          variant="prominent"
          buttonText={t('recharge.step3.proof.btn')}
          onFile={(blob) => setProofBlob(blob)}
          onClear={() => setProofBlob(null)}
          onError={(m) => setSubmitErr(m)}
          label={t('recharge.step3.proof.pickerLabel')}
          hint=""
        />

        {/* Icon-only flow so a player who can't read the labels still
            understands the sequence: open UPI app → find the payment →
            take a screenshot → upload here. Hidden once a file is
            chosen because the user has already done the steps. */}
        {!hasProof && (
          <div className="mt-3 rounded-xl bg-white/70 dark:bg-arena-900/30 ring-1 ring-inset ring-emerald-200/70 dark:ring-emerald-400/15
                          px-2 py-2.5
                          grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-1 text-emerald-700 dark:text-emerald-300">
            <div className="flex flex-col items-center gap-0.5 text-center">
              <Icon name="smartphone" size={22} filled className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[9.5px] font-bold uppercase tracking-wide leading-none">
                {t('recharge.step3.proof.flow.upi')}
              </span>
            </div>
            <Icon name="chevron_right" size={14} className="opacity-60" />
            <div className="flex flex-col items-center gap-0.5 text-center">
              <Icon name="receipt_long" size={22} filled className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[9.5px] font-bold uppercase tracking-wide leading-none">
                {t('recharge.step3.proof.flow.find')}
              </span>
            </div>
            <Icon name="chevron_right" size={14} className="opacity-60" />
            <div className="flex flex-col items-center gap-0.5 text-center">
              <Icon name="photo_camera" size={22} filled className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[9.5px] font-bold uppercase tracking-wide leading-none">
                {t('recharge.step3.proof.flow.snap')}
              </span>
            </div>
            <Icon name="chevron_right" size={14} className="opacity-60" />
            <div className="flex flex-col items-center gap-0.5 text-center">
              <Icon name="cloud_upload" size={22} filled className="text-emerald-600 dark:text-emerald-400" />
              <span className="text-[9.5px] font-bold uppercase tracking-wide leading-none">
                {t('recharge.step3.proof.flow.upload')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* OR divider — visually announces the either-or relationship
          between the prominent screenshot card above and the demoted
          transaction-ID card below. The two horizontal lines + small
          centered chip is a standard "alternative path" pattern that
          even non-readers parse correctly. */}
      <div className="flex items-center gap-3 px-2">
        <div className="flex-1 h-px bg-arena-200 dark:bg-arena-600/60" />
        <span className="text-[10px] uppercase tracking-[.2em] font-extrabold
                         text-arena-500 dark:text-neon-200/60
                         px-2 py-0.5 rounded-full
                         bg-arena-100 dark:bg-arena-700/40
                         border border-arena-200 dark:border-arena-600/40">
          {t('recharge.step3.or')}
        </span>
        <div className="flex-1 h-px bg-arena-200 dark:bg-arena-600/60" />
      </div>

      {/* ─── STEP B — TRANSACTION ID (secondary, smaller) ─────────
          Accepted instead of the screenshot — the player can submit
          on either piece of evidence. Visually demoted so the
          screenshot card stays the primary CTA. */}
      <div className="rounded-xl bg-arena-50/70 dark:bg-arena-900/40 border border-arena-200 dark:border-arena-600/40 px-3 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="shrink-0 w-7 h-7 rounded-lg grid place-items-center
                          bg-arena-300/70 dark:bg-arena-600 text-arena-700 dark:text-neon-100 font-extrabold text-[12px]">
            <Icon name="tag" size={14} filled />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-bold text-arena-700 dark:text-neon-200/80 leading-none">
              {t('recharge.step3.txnId')}
            </div>
            <div className="text-[10.5px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">
              {t('recharge.step3.txnIdHelp')}
            </div>
          </div>
        </div>
        <input
          value={txnId}
          onChange={(e) => setTxnId(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 80))}
          placeholder="e.g. 123456789012"
          className="w-full font-mono text-sm bg-white dark:bg-arena-800/60 border border-arena-300 dark:border-arena-600/50
                     rounded-lg px-3 py-2.5 outline-none focus:border-brand-400"
          maxLength={80}
        />
      </div>

      {/* Optional note — compact, no card */}
      <label className="block">
        <span className="block text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60 mb-1">
          {t('recharge.step3.note')} <span className="font-normal text-arena-400">{t('common.optional')}</span>
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 190))}
          placeholder={t('recharge.step3.notePlaceholder')}
          className="w-full text-sm bg-white dark:bg-arena-900/40 border border-arena-300 dark:border-arena-600/50
                     rounded-xl px-3 py-2.5 outline-none focus:border-brand-400"
          maxLength={190}
        />
      </label>

      {error && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30
                        text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="text-[11px] text-arena-500 dark:text-neon-200/60 leading-relaxed">
        {t('recharge.step3.disclaimer')}
        {cfg?.upi_id && <> · <span className="font-mono text-arena-700 dark:text-neon-300">{cfg.upi_id}</span></>}
      </div>
    </div>
  );
}

function StepSuccess({ requestId, amount, t }: {
  requestId: number | null; amount: number; t: ReturnType<typeof useT>;
}) {
  return (
    <div className="text-center py-3 space-y-3">
      <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center
                      bg-emerald-100 dark:bg-emerald-400/15
                      border border-emerald-300 dark:border-emerald-400/30
                      text-emerald-700 dark:text-emerald-300">
        <Icon name="hourglass_top" size={32} filled />
      </div>
      <h3 className="font-display font-extrabold text-lg text-arena-800 dark:text-neon-100">
        {t('recharge.success.title')}
      </h3>
      <p className="text-sm text-arena-600 dark:text-neon-200/80">
        {t('recharge.success.body', { amount: '₹' + amount.toLocaleString('en-IN') })}
      </p>
      {requestId != null && (
        <div className="text-[11px] text-arena-500 dark:text-neon-200/60">
          {t('recharge.success.refId')}: #<b className="font-mono">{requestId}</b>
        </div>
      )}
    </div>
  );
}

function Footer({
  step, busy, amount, canPayHere, canSubmit, parentLocked, step2CanConfirm,
  onBack, onNext, onSubmit, onClose, t,
}: {
  step: Step; busy: boolean; amount: number;
  canPayHere: boolean; canSubmit: boolean;
  // Cascade Recharge Lock — true when the server reports the parent
  // is auto-suspended. Suppresses the entire Step-2 footer (Back +
  // Next) because (a) advancing to Step 3 is meaningless without a
  // QR to pay against and (b) RechargeLockedPanel renders its own
  // Close CTA which is the only useful action while locked.
  parentLocked: boolean;
  // 2026-05-21 — gates Step 2's confirm button. False until the
  // player has tapped a UPI app AND the wait/visibility gate has
  // cleared. While false the footer hides the confirm CTA entirely
  // so the player must go back to the UPI app picker.
  step2CanConfirm: boolean;
  onBack: () => void; onNext: () => void; onSubmit: () => void; onClose: () => void;
  t: ReturnType<typeof useT>;
}) {
  // While locked on Step 2, render NOTHING — the panel handles its
  // own dismiss. We keep the footer container itself out of the tree
  // entirely so it doesn't add an empty border-top line.
  if (step === 2 && parentLocked) {
    return null;
  }
  return (
    <div className="px-4 py-3 border-t border-arena-200 dark:border-arena-600/40 shrink-0
                    flex items-center gap-2">
      {step === 1 && (
        <>
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 rounded-full text-sm font-bold text-arena-700 dark:text-neon-200
                       bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700">
            {t('common.cancel')}
          </button>
          <div className="flex-1" />
          <button type="button" onClick={onNext}
            disabled={amount < MIN_AMOUNT || amount > MAX_AMOUNT}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                       font-bold text-sm tracking-wider uppercase text-white
                       bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                       border border-white/15 active:scale-[.98] transition disabled:opacity-50">
            {t('common.next')} <Icon name="arrow_forward" size={16} filled />
          </button>
        </>
      )}
      {step === 2 && (
        <>
          <button type="button" onClick={onBack}
            className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full text-sm font-bold
                       text-arena-700 dark:text-neon-200
                       bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700">
            <Icon name="arrow_back" size={16} /> {t('common.back')}
          </button>
          <div className="flex-1" />
          {/* 2026-05-21 — Confirm CTA is now gated. Before the player
              taps a UPI app, the footer shows a disabled hint chip
              instead of a tappable button — that pushes them to use
              the UPI-app picker inside the body. Once step2CanConfirm
              flips, the big "I have paid" button slides in. */}
          {step2CanConfirm ? (
            <button type="button" onClick={onNext}
              disabled={!canPayHere}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full
                         font-extrabold text-[13px] tracking-wider uppercase text-white
                         bg-gradient-to-b from-emerald-500 via-emerald-600 to-emerald-700
                         border border-white/20 shadow-lg shadow-emerald-600/40
                         active:scale-[.98] transition disabled:opacity-50">
              <Icon name="check_circle" size={18} filled /> {t('recharge.step2.iPaid')}
            </button>
          ) : canPayHere ? (
            /* Escape hatch for QR-scan / second-device payers who never
               tap a UPI deep-link in THIS browser (so the reveal timer
               never starts). A subtle secondary link — the encouraged
               path (tap a UPI app → big green "I've paid") stays in the
               body — that still lets an already-paid player reach Step 3
               to enter their txn id / screenshot. */
            <button type="button" onClick={onNext}
              className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full
                         text-[12px] font-bold tracking-wide
                         text-arena-700 dark:text-neon-200
                         bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700
                         border border-arena-200 dark:border-arena-600/40 active:scale-[.98] transition">
              {t('recharge.step2.alreadyPaid')} <Icon name="arrow_forward" size={15} />
            </button>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full
                            text-[11px] font-bold uppercase tracking-wider
                            text-amber-800 dark:text-amber-200
                            bg-amber-100 dark:bg-amber-500/15
                            border border-amber-300 dark:border-amber-400/40">
              <Icon name="touch_app" size={14} filled />
              {t('recharge.step2.confirmLocked')}
            </div>
          )}
        </>
      )}
      {step === 3 && (
        <>
          <button type="button" onClick={onBack}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-3 rounded-full text-sm font-bold
                       text-arena-700 dark:text-neon-200
                       bg-arena-100 dark:bg-arena-700/60 hover:bg-arena-200 dark:hover:bg-arena-700 disabled:opacity-50">
            <Icon name="arrow_back" size={16} /> {t('common.back')}
          </button>
          <div className="flex-1" />
          {/* Bigger, taller, more colourful final submit so it's the
              obvious last action on the screen. Icon-first ordering
              (paper-plane → label) gives a clear "send for review"
              read even for users skimming icons. */}
          <button type="button" onClick={onSubmit}
            disabled={!canSubmit || busy}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full
                       font-extrabold text-[13px] tracking-wider uppercase text-white
                       bg-gradient-to-b from-emerald-500 via-emerald-600 to-emerald-700
                       hover:from-emerald-400 hover:via-emerald-500 hover:to-emerald-600
                       border border-white/20 shadow-lg shadow-emerald-600/40
                       active:scale-[.98] transition disabled:opacity-50 disabled:shadow-none">
            {busy ? <><Icon name="progress_activity" size={18} className="animate-spin" /> {t('common.sending')}</>
                  : <><Icon name="send" size={18} filled /> {t('recharge.step3.submit')}</>}
          </button>
        </>
      )}
      {step === 'success' && (
        <>
          <div className="flex-1" />
          <button type="button" onClick={onClose}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                       font-bold text-sm tracking-wider uppercase text-white
                       bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                       border border-white/15 active:scale-[.98] transition">
            {t('common.close')} <Icon name="check" size={16} filled />
          </button>
        </>
      )}
    </div>
  );
}
